// F-148 §4.1/§4.2 template-v1 CSV parser. All-or-nothing, client-side, formulas never
// evaluated, legacy code pages never guessed. Records are `string[]` read by index into typed
// literals; headers resolve through a frozen Map (prototype-pollution safe).

import { z } from 'zod';
import {
  RECEIVING_LIMITS,
  type ReceivingCsvFileErrorCode,
  type ReceivingCsvFileMeta,
  type ReceivingCsvParseResult,
  type ReceivingCsvRow,
  type ReceivingCsvRowError,
  type ReceivingCsvRowErrorCode,
  type ReceivingDraftLineInput,
} from '../types/stock-receiving';
import {
  cleanFreeText,
  codePointClass,
  normalizeKey,
  normalizeReceivingBarcode,
  normalizeReceivingEvidenceText,
  normalizeReceivingInteger,
  normalizeReceivingVariantRef,
} from './receiving-normalize';

export const RECEIVING_CSV_COLUMNS = [
  'barcode',
  'dukanti_variant_id',
  'supplier_code',
  'supplier_item_name',
  'unit_kind',
  'packs',
  'units_per_pack',
  'expected_units',
  'accepted_units',
  'damaged_units',
  'missing_units',
  'note',
] as const;

export type ReceivingCsvColumn = (typeof RECEIVING_CSV_COLUMNS)[number];

const COLUMN_INDEX: ReadonlyMap<string, ReceivingCsvColumn> = Object.freeze(
  new Map(RECEIVING_CSV_COLUMNS.map((c) => [c, c])),
);
const IDENTIFIER_OR_NAME_COLUMNS: readonly ReceivingCsvColumn[] = [
  'barcode',
  'dukanti_variant_id',
  'supplier_code',
  'supplier_item_name',
];
const PACK_TRIO: readonly ReceivingCsvColumn[] = ['unit_kind', 'packs', 'units_per_pack'];
const MAX_REPORTED_ERRORS = 50;

const REPLACEMENT_CHAR = String.fromCodePoint(0xfffd);
/** C0 controls except TAB/LF/CR (never inside a cell after line splitting), DEL and C1. */
const CONTROL_IN_CELL_RE = new RegExp(
  codePointClass([[0x0000, 0x0008], 0x000b, 0x000c, [0x000e, 0x001f], [0x007f, 0x009f]]),
  'u',
);

const rowSchema = z
  .object({
    sourceRowNumber: z
      .number()
      .int()
      .min(1)
      .max(RECEIVING_LIMITS.maxLines + 1),
    identifiers: z
      .object({
        barcode: z.string().nullable(),
        variantRef: z.string().nullable(),
        supplierCode: z.string().nullable(),
        supplierItemName: z.string().nullable(),
      })
      .strict(),
    unitKind: z.enum(['unit', 'pack']),
    packsExpected: z.number().int().min(1).max(RECEIVING_LIMITS.maxPacks).nullable(),
    unitsPerPack: z.number().int().min(1).max(RECEIVING_LIMITS.maxUnitsPerPack).nullable(),
    expectedUnits: z.number().int().min(1).max(RECEIVING_LIMITS.maxQuantity),
    acceptedUnits: z.number().int().min(0).max(RECEIVING_LIMITS.maxQuantity),
    damagedUnits: z.number().int().min(0).max(RECEIVING_LIMITS.maxQuantity),
    missingUnits: z.number().int().min(0).max(RECEIVING_LIMITS.maxQuantity),
    acceptedAuto: z.boolean(),
    note: z.string().nullable(),
  })
  .strict();

function fileFailure(fileError: ReceivingCsvFileErrorCode): ReceivingCsvParseResult {
  return { ok: false, fileError, rowErrors: [] };
}

function startsWith(bytes: Uint8Array, magic: number[]): boolean {
  if (bytes.length < magic.length) return false;
  return magic.every((b, i) => bytes[i] === b);
}

function decodeUtf8(bytes: Uint8Array): string | null {
  try {
    return new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    return null;
  }
}

type RecordParse =
  | { ok: true; fields: string[] }
  | { ok: false; code: 'bad_quote' | 'too_many_fields' };

/** RFC 4180 subset over one physical line (embedded CR/LF inside quotes is a row error). */
function parseRecord(line: string): RecordParse {
  const fields: string[] = [];
  let current = '';
  let i = 0;
  const n = line.length;
  while (i <= n) {
    if (i === n) {
      fields.push(current);
      break;
    }
    const ch = line[i];
    if (ch === '"') {
      if (current.length !== 0) return { ok: false, code: 'bad_quote' };
      i += 1;
      let closed = false;
      while (i < n) {
        const q = line[i];
        if (q === '"') {
          if (line[i + 1] === '"') {
            current += '"';
            i += 2;
            continue;
          }
          closed = true;
          i += 1;
          break;
        }
        current += q;
        i += 1;
      }
      if (!closed) return { ok: false, code: 'bad_quote' };
      if (i < n && line[i] !== ',') return { ok: false, code: 'bad_quote' };
      if (i === n) {
        fields.push(current);
        break;
      }
      // line[i] === ','
      fields.push(current);
      current = '';
      i += 1;
      if (fields.length > RECEIVING_LIMITS.maxCsvColumns)
        return { ok: false, code: 'too_many_fields' };
      continue;
    }
    if (ch === ',') {
      fields.push(current);
      current = '';
      i += 1;
      if (fields.length > RECEIVING_LIMITS.maxCsvColumns)
        return { ok: false, code: 'too_many_fields' };
      continue;
    }
    current += ch;
    i += 1;
  }
  if (fields.length > RECEIVING_LIMITS.maxCsvColumns) return { ok: false, code: 'too_many_fields' };
  return { ok: true, fields };
}

type HeaderParse =
  | { ok: true; columns: Array<ReceivingCsvColumn | null>; present: Set<ReceivingCsvColumn> }
  | { ok: false; fileError: ReceivingCsvFileErrorCode };

function parseHeader(line: string): HeaderParse {
  if (line.includes(REPLACEMENT_CHAR)) return { ok: false, fileError: 'invalid_character' };
  if (!line.includes(',') && line.includes(';'))
    return { ok: false, fileError: 'semicolon_delimiter' };
  const record = parseRecord(line);
  if (!record.ok) return { ok: false, fileError: 'unknown_header' };
  const columns: Array<ReceivingCsvColumn | null> = [];
  const present = new Set<ReceivingCsvColumn>();
  for (const raw of record.fields) {
    const name = normalizeKey(raw);
    const column = COLUMN_INDEX.get(name);
    if (!column) return { ok: false, fileError: 'unknown_header' };
    if (present.has(column)) return { ok: false, fileError: 'duplicate_header' };
    present.add(column);
    columns.push(column);
  }
  const hasIdentifier = IDENTIFIER_OR_NAME_COLUMNS.some((c) => present.has(c));
  const hasQuantity = present.has('expected_units') || PACK_TRIO.every((c) => present.has(c));
  if (!hasIdentifier || !hasQuantity) return { ok: false, fileError: 'missing_headers' };
  return { ok: true, columns, present };
}

interface RowContext {
  rowNumber: number;
  errors: ReceivingCsvRowError[];
}

function pushError(
  ctx: RowContext,
  column: ReceivingCsvColumn | null,
  code: ReceivingCsvRowErrorCode,
): void {
  ctx.errors.push({ row: ctx.rowNumber, column, code });
}

function cellOf(
  fields: string[],
  columns: Array<ReceivingCsvColumn | null>,
  column: ReceivingCsvColumn,
): string {
  const index = columns.indexOf(column);
  if (index < 0 || index >= fields.length) return '';
  return fields[index] ?? '';
}

function parseRow(
  fields: string[],
  columns: Array<ReceivingCsvColumn | null>,
  rowNumber: number,
): { row: ReceivingCsvRow | null; errors: ReceivingCsvRowError[] } {
  const ctx: RowContext = { rowNumber, errors: [] };
  const cell = (c: ReceivingCsvColumn): string => cellOf(fields, columns, c);

  // Character-level checks per cell (before normalization).
  for (const column of RECEIVING_CSV_COLUMNS) {
    const value = cell(column);
    if (value.includes(REPLACEMENT_CHAR)) pushError(ctx, column, 'invalid_character');
    else if (CONTROL_IN_CELL_RE.test(value)) pushError(ctx, column, 'control_character');
  }
  if (ctx.errors.length > 0) return { row: null, errors: ctx.errors };

  const barcode = normalizeReceivingBarcode(cell('barcode'));
  if (!barcode.ok) pushError(ctx, 'barcode', barcode.error);
  const variantRef = normalizeReceivingVariantRef(cell('dukanti_variant_id'));
  if (!variantRef.ok) pushError(ctx, 'dukanti_variant_id', variantRef.error);
  const supplierCode = normalizeReceivingEvidenceText(cell('supplier_code'));
  if (!supplierCode.ok) pushError(ctx, 'supplier_code', supplierCode.error);
  const supplierItemName = cleanFreeText(cell('supplier_item_name'), RECEIVING_LIMITS.maxTextField);
  if (!supplierItemName.ok) pushError(ctx, 'supplier_item_name', supplierItemName.error);
  const note = cleanFreeText(cell('note'), RECEIVING_LIMITS.maxNote);
  if (!note.ok) pushError(ctx, 'note', note.error);

  const unitKindKey = normalizeKey(cell('unit_kind'));
  let unitKind: 'unit' | 'pack' = 'unit';
  if (unitKindKey === 'pack') unitKind = 'pack';
  else if (unitKindKey !== '' && unitKindKey !== 'unit')
    pushError(ctx, 'unit_kind', 'invalid_unit_kind');

  const packs = normalizeReceivingInteger(cell('packs'), {
    min: 1,
    max: RECEIVING_LIMITS.maxPacks,
  });
  if (!packs.ok) pushError(ctx, 'packs', packs.error);
  const unitsPerPack = normalizeReceivingInteger(cell('units_per_pack'), {
    min: 1,
    max: RECEIVING_LIMITS.maxUnitsPerPack,
  });
  if (!unitsPerPack.ok) pushError(ctx, 'units_per_pack', unitsPerPack.error);
  const expected = normalizeReceivingInteger(cell('expected_units'), {
    min: 1,
    max: RECEIVING_LIMITS.maxQuantity,
  });
  if (!expected.ok) pushError(ctx, 'expected_units', expected.error);
  const dispositionRange = { min: 0, max: RECEIVING_LIMITS.maxQuantity };
  const accepted = normalizeReceivingInteger(cell('accepted_units'), dispositionRange);
  if (!accepted.ok) pushError(ctx, 'accepted_units', accepted.error);
  const damaged = normalizeReceivingInteger(cell('damaged_units'), dispositionRange);
  if (!damaged.ok) pushError(ctx, 'damaged_units', damaged.error);
  const missing = normalizeReceivingInteger(cell('missing_units'), dispositionRange);
  if (!missing.ok) pushError(ctx, 'missing_units', missing.error);

  if (ctx.errors.length > 0) return { row: null, errors: ctx.errors };
  // All results are ok from here on; narrow once.
  if (
    !barcode.ok ||
    !variantRef.ok ||
    !supplierCode.ok ||
    !supplierItemName.ok ||
    !note.ok ||
    !packs.ok ||
    !unitsPerPack.ok ||
    !expected.ok ||
    !accepted.ok ||
    !damaged.ok ||
    !missing.ok
  ) {
    return { row: null, errors: ctx.errors };
  }

  let expectedUnits: number | null = expected.value;
  let packsExpected: number | null = null;
  let unitsPerPackValue: number | null = null;
  if (unitKind === 'pack') {
    if (packs.value == null || unitsPerPack.value == null) {
      pushError(ctx, packs.value == null ? 'packs' : 'units_per_pack', 'pack_incomplete');
    } else {
      packsExpected = packs.value;
      unitsPerPackValue = unitsPerPack.value;
      const product = packs.value * unitsPerPack.value;
      if (product > RECEIVING_LIMITS.maxQuantity) pushError(ctx, 'expected_units', 'out_of_range');
      else if (expectedUnits != null && expectedUnits !== product) {
        pushError(ctx, 'expected_units', 'pack_total_mismatch');
      } else expectedUnits = product;
    }
  } else {
    if (packs.value != null) pushError(ctx, 'packs', 'invalid_unit_kind');
    if (unitsPerPack.value != null) pushError(ctx, 'units_per_pack', 'invalid_unit_kind');
    if (expectedUnits == null) pushError(ctx, 'expected_units', 'not_integer');
  }
  if (ctx.errors.length > 0 || expectedUnits == null) return { row: null, errors: ctx.errors };

  const dispositions = [accepted.value, damaged.value, missing.value];
  const filled = dispositions.filter((v) => v != null).length;
  let acceptedUnits: number;
  let damagedUnits: number;
  let missingUnits: number;
  let acceptedAuto: boolean;
  if (filled === 0) {
    acceptedUnits = expectedUnits;
    damagedUnits = 0;
    missingUnits = 0;
    acceptedAuto = true;
  } else if (
    filled === 3 &&
    accepted.value != null &&
    damaged.value != null &&
    missing.value != null
  ) {
    acceptedUnits = accepted.value;
    damagedUnits = damaged.value;
    missingUnits = missing.value;
    acceptedAuto = false;
  } else {
    const firstBlank: ReceivingCsvColumn =
      accepted.value == null
        ? 'accepted_units'
        : damaged.value == null
          ? 'damaged_units'
          : 'missing_units';
    pushError(ctx, firstBlank, 'partial_disposition');
    return { row: null, errors: ctx.errors };
  }

  const candidate: ReceivingCsvRow = {
    sourceRowNumber: rowNumber,
    identifiers: {
      barcode: barcode.value,
      variantRef: variantRef.value,
      supplierCode: supplierCode.value,
      supplierItemName: supplierItemName.value,
    },
    unitKind,
    packsExpected,
    unitsPerPack: unitsPerPackValue,
    expectedUnits,
    acceptedUnits,
    damagedUnits,
    missingUnits,
    acceptedAuto,
    note: note.value,
  };
  const parsed = rowSchema.safeParse(candidate);
  if (!parsed.success) {
    pushError(ctx, null, 'out_of_range');
    return { row: null, errors: ctx.errors };
  }
  return { row: parsed.data, errors: [] };
}

/**
 * Parses template-v1 bytes (§4.2). Returns rows only when the whole file is valid; otherwise the
 * file error and/or the first 50 row errors. Any error means the caller must not call the RPC.
 */
export function parseReceivingCsv(
  bytes: Uint8Array,
  fileName: string,
  _mimeType: string,
): ReceivingCsvParseResult {
  if (bytes.byteLength > RECEIVING_LIMITS.maxCsvBytes) return fileFailure('too_large');
  if (bytes.byteLength === 0) return fileFailure('empty_file');
  if (startsWith(bytes, [0x50, 0x4b, 0x03, 0x04])) return fileFailure('unsupported_format');
  if (startsWith(bytes, [0x25, 0x50, 0x44, 0x46])) return fileFailure('unsupported_format');
  if (startsWith(bytes, [0xd0, 0xcf, 0x11, 0xe0])) return fileFailure('unsupported_format');
  if (startsWith(bytes, [0xff, 0xfe]) || startsWith(bytes, [0xfe, 0xff])) {
    return fileFailure('utf16_encoding');
  }

  const body = startsWith(bytes, [0xef, 0xbb, 0xbf]) ? bytes.subarray(3) : bytes;
  if (body.byteLength === 0) return fileFailure('empty_file');
  const text = decodeUtf8(body);
  if (text === null) return fileFailure('encoding_not_utf8');

  const lines = text.split(/\r\n|\n/u);
  let headerIndex = 0;
  while (headerIndex < lines.length && (lines[headerIndex] ?? '').trim() === '') headerIndex += 1;
  const headerLine = lines[headerIndex];
  if (headerLine === undefined) return fileFailure('empty_file');

  const header = parseHeader(headerLine);
  if (!header.ok) return fileFailure(header.fileError);

  const rows: ReceivingCsvRow[] = [];
  const rowErrors: ReceivingCsvRowError[] = [];
  let rowNumber = 0;
  for (let i = headerIndex + 1; i < lines.length; i += 1) {
    const raw = lines[i];
    if (raw === undefined || raw.length === 0 || raw.trim() === '') continue;
    rowNumber += 1;
    if (rowNumber > RECEIVING_LIMITS.maxLines) return fileFailure('too_many_rows');
    if (raw.includes('\r')) {
      rowErrors.push({ row: rowNumber, column: null, code: 'bad_quote' });
      continue;
    }
    const record = parseRecord(raw);
    if (!record.ok) {
      rowErrors.push({ row: rowNumber, column: null, code: record.code });
      continue;
    }
    if (record.fields.length > header.columns.length) {
      rowErrors.push({ row: rowNumber, column: null, code: 'too_many_fields' });
      continue;
    }
    const parsed = parseRow(record.fields, header.columns, rowNumber);
    if (parsed.row) rows.push(parsed.row);
    else rowErrors.push(...parsed.errors);
  }

  if (rowErrors.length > 0) {
    return { ok: false, fileError: null, rowErrors: rowErrors.slice(0, MAX_REPORTED_ERRORS) };
  }
  if (rows.length === 0) return fileFailure('empty_file');

  const fileMeta: ReceivingCsvFileMeta = {
    fileName: Array.from(fileName).slice(0, 120).join(''),
    byteSize: bytes.byteLength,
    rowCount: rows.length,
  };
  return { ok: true, rows, fileMeta };
}

/** Maps a validated CSV row to a draft line input (§4.1: imported pack rows start unconfirmed). */
export function mapReceivingCsvRowToLineInput(
  row: ReceivingCsvRow,
  clientLineId: string,
): ReceivingDraftLineInput {
  return {
    clientLineId,
    source: 'csv',
    sourceRowNumber: row.sourceRowNumber,
    identifiers: {
      barcode: row.identifiers.barcode,
      variantRef: row.identifiers.variantRef,
      supplierCode: row.identifiers.supplierCode,
      supplierItemName: row.identifiers.supplierItemName,
    },
    variantId: null,
    matchMethod: null,
    unitKind: row.unitKind,
    packsExpected: row.packsExpected,
    unitsPerPack: row.unitsPerPack,
    unitsPerPackConfirmed: null,
    expectedUnits: row.expectedUnits,
    acceptedUnits: row.acceptedUnits,
    damagedUnits: row.damagedUnits,
    missingUnits: row.missingUnits,
    note: row.note,
    acceptedAuto: row.acceptedAuto,
  };
}
